/**
 * Die Karte „Echtzeit-Test“: Soniox gegen ElevenLabs v4 Turbo. (07.10.2026, Bahn eleven-echtzeit)
 *
 * Martin, 07.10.: *„… mal das Ergebnis in Echtzeit-Kommunikation testen.“* Eine Taste liest EINEN festen
 * Satz (80 Zeichen) nacheinander über Soniox und über ElevenLabs v4 Turbo vor — über denselben PCM-Spieler
 * wie das Vorlesen (lib/tts-pcm.ts) — und zeigt danach, wann jeweils der erste Ton kam:
 *
 *   Soniox: erster Ton nach 310 ms · ElevenLabs v4 Turbo: 140 ms
 *
 * Darüber stehen die Stimmen, die verglichen werden („Soniox: Nathan · ElevenLabs: Deine Stimme“) — der Server
 * wählt sie (server/tts-echtzeit-test.ts, `testStimmen`), die Namen kommen aus VOICE_OPTIONS (lib/settings.ts).
 *
 * Die Zahlen sind die des Servers (vom Abruf bis zum ersten Ton-Byte des Dienstes) — dieselben, die als
 * `[tts-latenz] … weg=test` im Journal stehen (server/tts-echtzeit-test.ts).
 *
 * EINBAU: `echtzeitTestKarte()` liefert die fertige Karte; sie gehört in die Einstellungen, nur für Konto 1
 * (der Server weist alle anderen ohnehin ab). Keine Inline-Styles: Aussehen in frontend/style.css
 * („ECHTZEIT-TEST“).
 */
import { VOICE_OPTIONS } from "./settings.js";
import { PcmStueck, entsperrePcm } from "./tts-pcm.js";

const ECHTZEIT_TEST_PFAD = "/api/tts/echtzeit-test";

interface TestZeit {
  erstesByteMs: number | null;
  endeMs: number;
  ok: boolean;
  grund: string | null;
}
interface TestAnbieter {
  id: "soniox" | "eleven";
  name: string;
  /** Die Stimm-Kennung, die der Server für diesen Lauf gewählt hat. */
  stimme?: string;
}

/**
 * Ein Grund des Servers in Worte für den Menschen — nur, was der Grund wirklich hergibt:
 *   401 (Handschlag oder Fehler-Nachricht): der Schlüssel wurde abgelehnt; WARUM, sagt der Dienst nicht.
 *   403: „Kein Echtzeit-Recht“ nur, wenn der Dienst selbst von fehlendem Recht spricht (permission, forbidden).
 */
export function grundText(grund: string | null): string {
  const g = grund ?? "";
  if (/^401/.test(g)) return "Schlüssel abgelehnt (ungültig oder ohne Echtzeit-Recht)";
  if (/^402|quota|credit|insufficient|balance|payment/.test(g)) return "Guthaben leer";
  if (/^403-.*(permission|forbidden)/.test(g)) return "Kein Echtzeit-Recht";
  if (/^403/.test(g)) return "Zugang abgelehnt (403)";
  if (/^429/.test(g)) return "Zu viele Verbindungen";
  if (/kein-schluessel/.test(g)) return "Kein Schlüssel hinterlegt";
  if (/frist|haengt/.test(g)) return "Keine Antwort rechtzeitig";
  if (/abbruch/.test(g)) return "Abgebrochen";
  return "Gescheitert";
}

/** Der Name einer Stimme für die Anzeige: aus VOICE_OPTIONS, sonst der Soniox-Name oder „eigene Stimme“. */
export function stimmName(id: string | undefined): string {
  if (!id) return "–";
  const o = VOICE_OPTIONS.find((v) => v.id === id);
  if (o) return o.label;
  return id.startsWith("soniox:") ? id.slice("soniox:".length) : "eigene Stimme";
}

/** „Soniox: Nathan · ElevenLabs: Deine Stimme“ */
export function stimmenZeile(anbieter: TestAnbieter[]): string {
  return anbieter.map((a) => `${a.id === "eleven" ? "ElevenLabs" : "Soniox"}: ${stimmName(a.stimme)}`).join(" · ");
}

/** „erster Ton nach 310 ms“ — oder warum nicht. */
export function zeitText(z: TestZeit | null | undefined): string {
  if (!z) return "nicht gelaufen";
  if (!z.ok || z.erstesByteMs === null) return grundText(z.grund);
  return `erster Ton nach ${Math.round(z.erstesByteMs)} ms`;
}

/** Die eine Ergebniszeile: „Soniox: erster Ton nach 310 ms · ElevenLabs v4 Turbo: 140 ms“. */
export function ergebnisZeile(anbieter: TestAnbieter[], ergebnis: Partial<Record<string, TestZeit | null>>): string {
  return anbieter
    .map((a, i) => {
      const t = zeitText(ergebnis[a.id]);
      return `${a.name}: ${i > 0 ? t.replace(/^erster Ton nach /, "") : t}`;
    })
    .join(" · ");
}

export interface EchtzeitTestUmgebung {
  holen: typeof fetch;
  /** Spielt eine PCM-Adresse ab und kehrt nach dem Ende zurück. Wirft nie. */
  spiele: (url: string, signal: AbortSignal) => Promise<void>;
  /** Die Geste entsperrt den Ton (iOS). */
  entsperren: () => void;
}

function standardUmgebung(): EchtzeitTestUmgebung {
  return {
    holen: (...a) => fetch(...a),
    spiele: async (url, signal) => {
      try {
        await new PcmStueck(url, signal).spiele({});
      } catch {
        /* Ein Fehler steht danach in den Zeiten des Servers. */
      }
    },
    entsperren: () => entsperrePcm(true),
  };
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/**
 * Die Karte. EINE Einbaustelle: das gelieferte Element in die Einstellungen hängen (nur Konto 1).
 */
export function echtzeitTestKarte(umgebung: EchtzeitTestUmgebung = standardUmgebung()): HTMLElement {
  const karte = el("section", "settings-subsection echtzeit-test");
  karte.append(el("h3", "settings-subsection-title", "Echtzeit-Test"));
  karte.append(
    el(
      "p",
      "settings-modules-hint",
      "Liest einen festen Satz nacheinander mit Soniox und mit ElevenLabs v4 Turbo vor und zeigt, wann jeweils der erste Ton kam. Kostet wenige ElevenLabs-Credits (80 Zeichen je Lauf), höchstens 5 Läufe pro Stunde.",
    ),
  );

  const stimmen = el("p", "echtzeit-test__stimmen");
  stimmen.hidden = true;
  const bahnen = el("div", "echtzeit-test__bahnen");
  bahnen.hidden = true;
  const knopf = el("button", "settings-btn echtzeit-test__knopf", "Echtzeit-Test starten");
  knopf.type = "button";
  const status = el("p", "echtzeit-test__status");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  karte.append(knopf, stimmen, bahnen, status);

  const zeigeBahnen = (anbieter: TestAnbieter[], ergebnis: Partial<Record<string, TestZeit | null>>): void => {
    const werte = anbieter.map((a) => ergebnis[a.id]).filter((z): z is TestZeit => !!z && z.ok && z.erstesByteMs !== null);
    const max = Math.max(1, ...werte.map((z) => z.erstesByteMs ?? 0));
    bahnen.replaceChildren(
      ...anbieter.map((a) => {
        const z = ergebnis[a.id];
        const zeile = el("div", "echtzeit-test__bahn");
        zeile.dataset.anbieter = a.id;
        const name = el("span", "echtzeit-test__name", a.name);
        name.append(" ", el("span", "echtzeit-test__stimme", `· ${stimmName(a.stimme)}`));
        const messer = el("meter", "echtzeit-test__messer");
        // Als Attribute: ein Messbalken ohne Inline-Style, die Länge ist die Zeit bis zum ersten Ton.
        messer.setAttribute("min", "0");
        messer.setAttribute("max", String(Math.round(max)));
        messer.setAttribute("value", String(z?.ok && z.erstesByteMs !== null ? Math.round(z.erstesByteMs) : 0));
        messer.setAttribute("aria-label", `${a.name}: ${zeitText(z)}`);
        const zahl = el("span", "echtzeit-test__zahl", z?.ok && z.erstesByteMs !== null ? `${Math.round(z.erstesByteMs)} ms` : "–");
        if (!z?.ok) zeile.dataset.fehler = "ja";
        zeile.append(name, messer, zahl);
        return zeile;
      }),
    );
    bahnen.hidden = false;
  };

  knopf.addEventListener("click", () => {
    umgebung.entsperren();
    knopf.disabled = true;
    void (async () => {
      const ac = new AbortController();
      try {
        status.textContent = "Startet …";
        const r = await umgebung.holen(ECHTZEIT_TEST_PFAD, { method: "POST", credentials: "same-origin" });
        const j = (await r.json().catch(() => ({}))) as {
          ok?: boolean;
          lauf?: string;
          anbieter?: TestAnbieter[];
          grund?: string;
          wiederInMin?: number;
        };
        if (!r.ok || !j.lauf || !Array.isArray(j.anbieter)) {
          status.textContent =
            r.status === 429
              ? `Fünf Läufe in dieser Stunde sind erreicht — wieder in ${j.wiederInMin ?? 60} Minuten.`
              : r.status === 401 || r.status === 403
                ? "Nur für Konto 1."
                : "Der Test ließ sich nicht starten.";
          return;
        }
        stimmen.textContent = stimmenZeile(j.anbieter);
        stimmen.hidden = false;
        for (const a of j.anbieter) {
          status.textContent = `${a.name} (${stimmName(a.stimme)}) spricht …`;
          await umgebung.spiele(
            `${ECHTZEIT_TEST_PFAD}/pcm?lauf=${encodeURIComponent(j.lauf)}&anbieter=${encodeURIComponent(a.id)}`,
            ac.signal,
          );
        }
        const e = await umgebung.holen(`${ECHTZEIT_TEST_PFAD}?lauf=${encodeURIComponent(j.lauf)}`, { credentials: "same-origin" });
        const ej = (await e.json().catch(() => ({}))) as { ergebnis?: Partial<Record<string, TestZeit | null>> };
        const ergebnis = ej.ergebnis ?? {};
        zeigeBahnen(j.anbieter, ergebnis);
        status.textContent = ergebnisZeile(j.anbieter, ergebnis);
      } catch {
        status.textContent = "Der Test ist abgebrochen.";
      } finally {
        ac.abort();
        knopf.disabled = false;
      }
    })();
  });
  return karte;
}
